import { Pencil, Plus, TriangleAlert, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { servicesApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { OFFER_LIST_COPY } from '../utils/institutionOffer'
import { NEED_CATEGORY_LABELS, serviceTypeFor, summarizeJson } from '../utils/taxonomy'
import { ServiceForm } from './ServiceForm'
import type { ProviderServiceResponse } from '../types/api'

const TUTOR_COPY = {
  heading: 'What I teach',
  blurb: 'One entry per subject area - learners discover you by these.',
  addLabel: 'Add a service',
}

export function ServicesSection({ services, onChanged }: { services: ProviderServiceResponse[]; onChanged: () => void }) {
  const { profile } = useAuth()
  const copy = OFFER_LIST_COPY[profile?.entityKind ?? ''] ?? TUTOR_COPY
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  async function handleDelete(id: string) {
    if (!confirm('Remove this service?')) return
    await servicesApi.remove(id)
    onChanged()
  }

  return (
    <div id="services">
      {/* A tutor teaches subjects; a school admits classes and a company offers internships.
          Same list, named in the entity's own words. */}
      <h2 className="text-lg font-bold text-ink-900">{copy.heading}</h2>
      <p className="mb-3 text-sm text-ink-500">{copy.blurb}</p>

      {services.length === 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-xl border border-sun-400/50 bg-sun-400/10 px-3 py-2 text-sm text-amber-900">
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          You have not added what you teach yet. Add a category and subject below so the map can
          match you with the right learners.
        </p>
      )}

      <div className="space-y-3">
        {services.map((service) =>
          editingId === service.id ? (
            <ServiceForm
              key={service.id}
              initial={service}
              onCancel={() => setEditingId(null)}
              onSaved={() => {
                setEditingId(null)
                onChanged()
              }}
            />
          ) : (
            <div key={service.id} className="card-pad flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-ink-900">{NEED_CATEGORY_LABELS[service.category]}</span>
                  {service.isPrimary && <span className="pill bg-sun-400/20 text-amber-800">Primary</span>}
                  {/* Derived from the category, not a separate choice - see serviceTypeFor. */}
                  <span className="pill bg-ink-100 text-ink-700">{serviceTypeFor(service.category)}</span>
                </div>
                <p className="text-sm text-ink-500">
                  {[summarizeJson(service.items), summarizeJson(service.levels)].filter(Boolean).join(' · ')}
                </p>
                {/* Without this, availability and fee are write-only: saved but never shown back. */}
                <p className="text-sm text-ink-400">
                  {[
                    summarizeJson(service.modes),
                    summarizeJson(service.days),
                    summarizeJson(service.timings),
                    service.feeBand,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <button onClick={() => setEditingId(service.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 hover:bg-ink-100">
                  <Pencil size={14} />
                </button>
                <button onClick={() => handleDelete(service.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 hover:bg-ink-100">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ),
        )}
      </div>

      {adding ? (
        <div className="mt-3">
          <ServiceForm
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false)
              onChanged()
            }}
          />
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="btn-ghost mt-3 gap-1.5">
          <Plus size={15} /> {copy.addLabel}
        </button>
      )}
    </div>
  )
}
